'use client'
/**
 * MasjidOsProClient — the PUBLIC page a PRO/WAQF masjid gets. Rendered ONLY for pro/waqf tiers
 * (page.tsx gate); basic/none keep the existing MasjidProfileClient.
 *
 * BRAND: re-skinned to be a SIBLING of the basic masjid detail page (MasjidProfileClient) — the
 * live "Fable" brand. Reuses that page's shell (page.module.css `.shell` + `.hero`), section header
 * (DashboardSection, with the emerald→gold underline), cards (DashboardSection / PremiumCard),
 * pills (StatusBadge), and buttons (Button/ButtonLink). Light ivory body, dark hero band, Poppins
 * headings, Inter body, JetBrains Mono ONLY for data figures (amounts, times, %).
 *
 * HARD RAILS (unchanged): all donate/contribute → DonateFlow (masjids.upi_id) only; committee =
 * names/roles only, contact request-gated; transparency/bill-alert → approved ledger only; bill
 * alert only on a real approved-period deficit; social = real profile links or nothing.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import {
  Award, ShieldCheck, BadgeCheck, MapPin, Star, Share2, Navigation, Clock, Heart, Zap, Target,
  Users, HeartHandshake, HandHelping, UserPlus, Instagram, Facebook, Droplets,
  Snowflake, Car, BookOpen, Accessibility, type LucideIcon,
} from 'lucide-react'
import DonateFlow from './DonateFlow'
import { Button, ButtonLink, DashboardSection, StatusBadge, MetricPill, PremiumCard } from '../../../components/ui'
import { segColors } from '../../../design/colors'
import { masjidPlaceholder } from '../../../lib/masjid-placeholder'
import styles from './page.module.css'

const MONO = 'var(--f-mono)'
// Light-body text/accents (semantic tokens shared with the basic page).
const NAVY = 'var(--color-navy-900)'
const SEC = 'var(--text-secondary)'
const MUT = 'var(--text-muted)'
const LINE = 'var(--border-light)'
const GREEN = '#0E7C61'
const GOLD = '#B8932B'      // gold text on light (AA)
const GOLD_BRIGHT = '#D4AF37'
const DANGER = '#C0432B'
const figure: CSSProperties = { fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }
const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const inrSpaced = (n: number) => `₹ ${Math.round(n).toLocaleString('en-IN')}`

// ── Types (mirror the SSR loaders in page.tsx) ───────────────────────────────
type CategoryTotal = { category: string; type: 'income' | 'expense'; total: number }
type Period = { period: string; categoryTotals: CategoryTotal[]; income: number; expense: number; balance: number; approvedAt: string | null }
type Campaign = { id: string; title: string; goal: number; raised: number; endsAt: string | null }
type Announcement = { id: string; title: string; category: string; createdAt: string | null }
type Member = { role: string; label: string; name: string; photoUrl: string | null }
type Masjid = {
  id: string; slug: string; nameEnglish: string; city: string | null; district: string | null; state: string | null
  addressLine1: string | null; verified: boolean | null; description: string | null; upiId: string | null
  facebookUrl: string | null; instagramUrl: string | null; youtubeUrl: string | null
  latitude: any; longitude: any; facilities: any; capacity: number | null; establishedYear: number | null
  coverPhoto?: string | null; photos?: any; isDemo?: boolean
}

/** Parse a YouTube URL → embeddable video id (or null). Mirrors MasjidProfileClient.parseYoutube. */
function normalizeLink(url: string): string { return /^https?:\/\//i.test(url) ? url : `https://${url}` }
function youtubeVideoId(raw: string | null | undefined): string | null {
  const value = String(raw || '').trim()
  if (!value) return null
  let u: URL
  try { u = new URL(normalizeLink(value)) } catch { return null }
  const host = u.hostname.replace(/^www\./, '').toLowerCase()
  const yt = host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com' || host === 'youtu.be'
  if (!yt) return null
  const idOk = (id: string) => /^[A-Za-z0-9_-]{6,}$/.test(id)
  const parts = u.pathname.split('/').filter(Boolean)
  if (host === 'youtu.be') return parts[0] && idOk(parts[0]) ? parts[0] : null
  const v = u.searchParams.get('v')
  if (v && idOk(v)) return v
  if ((parts[0] === 'embed' || parts[0] === 'shorts') && parts[1] && idOk(parts[1])) return parts[1]
  return null // channel/handle URL → not embeddable → honest-empty (no video card)
}
type PrayerTimes = { Fajr?: string; Dhuhr?: string; Asr?: string; Maghrib?: string; Isha?: string } | null

// ── Small helpers ────────────────────────────────────────────────────────────
function fmt12(t?: string): string {
  if (!t || !/^\d{1,2}:\d{2}/.test(t)) return '—'
  const [h, m] = t.split(':').map(Number)
  const ap = h >= 12 ? 'PM' : 'AM'
  const hh = h % 12 || 12
  return `${hh}:${String(m).padStart(2, '0')} ${ap}`
}
function monthLabel(period: string): string {
  const [y, m] = period.split('-').map(Number)
  if (!y || !m) return period
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}
function dateLabel(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
function relTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso).getTime()
  if (Number.isNaN(d)) return ''
  const mins = Math.max(0, Math.round((Date.now() - d) / 60000))
  if (mins < 60) return `${mins}m ago`
  const h = Math.round(mins / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}
function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'M'
}
const ACT: Record<string, { color: string; label: string }> = {
  general: { color: GOLD_BRIGHT, label: 'Announcement' },
  event: { color: GREEN, label: 'Event' },
  lecture: { color: GOLD_BRIGHT, label: 'Lecture' },
  urgent: { color: DANGER, label: 'Urgent' },
  janaza: { color: DANGER, label: 'Funeral prayer notice' },
  help: { color: DANGER, label: 'Khidmah' },
}
const FACILITY_ICON: Record<string, LucideIcon> = {
  wudu: Droplets, ablution: Droplets, ac: Snowflake, 'air conditioning': Snowflake, parking: Car,
  library: BookOpen, wheelchair: Accessibility, 'wheelchair accessible': Accessibility,
}

function EmptyFrame({ icon: Icon, title, sub }: { icon: LucideIcon; title: string; sub: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '22px 14px' }}>
      <div style={{ width: 46, height: 46, borderRadius: 12, margin: '0 auto', display: 'grid', placeItems: 'center', background: 'rgba(14,124,97,.08)', border: `1px solid ${LINE}` }}>
        <Icon size={22} strokeWidth={1.5} style={{ color: GREEN }} />
      </div>
      <p style={{ margin: '10px 0 3px', fontSize: 14.5, fontWeight: 700, color: NAVY }}>{title}</p>
      <p style={{ margin: 0, fontSize: 12.5, color: SEC, lineHeight: 1.5 }}>{sub}</p>
    </div>
  )
}

// ── Transparency donut (light) ───────────────────────────────────────────────
function Donut({ segments, size = 172 }: { segments: { value: number; color: string }[]; size?: number }) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1
  const R = 56, C = 2 * Math.PI * R
  let acc = 0
  return (
    <svg viewBox="0 0 140 140" width={size} height={size} role="img" aria-label={`Total spent ${inr(total)}`}>
      <circle cx={70} cy={70} r={R} fill="none" stroke="rgba(11,19,43,.06)" strokeWidth={18} />
      {segments.map((s, i) => {
        const dash = (s.value / total) * C
        const el = (
          <circle key={i} cx={70} cy={70} r={R} fill="none" stroke={s.color} strokeWidth={18}
            strokeDasharray={`${Math.max(dash - 2, 0)} ${C - Math.max(dash - 2, 0)}`} strokeDashoffset={-acc}
            transform="rotate(-90 70 70)" strokeLinecap="round" />
        )
        acc += dash
        return el
      })}
      <text x={70} y={65} textAnchor="middle" fontSize={9} letterSpacing="1" fill="#6B7690">Total Spent</text>
      <text x={70} y={84} textAnchor="middle" style={{ fontFamily: 'var(--f-mono)' }} fontSize={15} fontWeight={700} fill="#0B132B">{inr(total)}</text>
    </svg>
  )
}

function StatRow({ label, value, color = NAVY }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13 }}>
      <span style={{ color: SEC }}>{label}</span>
      <span style={{ ...figure, color, fontWeight: 700 }}>{value}</span>
    </div>
  )
}

/* Sidebar social feed — HONEST real-or-empty:
 *   • no URL for this platform  → renders NOTHING (section hidden).
 *   • real (non-demo) masjid + URL → header + a real "View profile" link to their actual profile.
 *     We have NO IG/FB Graph integration, so we NEVER fabricate posts for a real masjid.
 *   • demo masjid (is_demo) + URL → the same real link PLUS sample thumbnails (clearly labelled a
 *     sample), sourced from the demo masjid's own seeded photos. Real masjids never see these.
 * When a real feed integration lands, swap the demo-sample block for real posts — the URL gate and
 * honest-empty behaviour stay identical. */
function SocialFeed({ platform, url, icon: Icon, tint, isDemo, samples }: {
  platform: string; url: string | null | undefined; icon: LucideIcon; tint: string; isDemo: boolean; samples: string[]
}) {
  if (!url || !String(url).trim()) return null // honest-empty — no section at all
  const href = normalizeLink(String(url))
  const showSamples = isDemo && samples.length > 0
  return (
    <DashboardSection title={platform} eyebrow={showSamples ? 'Sample feed · demo' : 'Connected profile'}>
      {showSamples ? (
        <div className={styles.proFeedGrid}>
          {samples.slice(0, 4).map((src, i) => (
            <div key={i} className={styles.proFeedThumb}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={src} alt="" loading="lazy" />
            </div>
          ))}
        </div>
      ) : null}
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: NAVY }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center', background: `${tint}1a`, border: `1px solid ${tint}40`, flexShrink: 0 }}>
          <Icon size={17} strokeWidth={1.75} style={{ color: tint }} />
        </span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700 }}>View profile ↗</span>
          <span style={{ display: 'block', fontSize: 11.5, color: SEC, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{href.replace(/^https?:\/\/(www\.)?/, '')}</span>
        </span>
      </a>
      {showSamples ? <p style={{ margin: '10px 0 0', fontSize: 10.5, color: MUT, lineHeight: 1.5 }}>Sample content for this demo masjid. A connected masjid shows its real feed here.</p> : null}
    </DashboardSection>
  )
}

export default function MasjidOsProClient(props: {
  slug: string; masjid: Masjid; tier: string; prayerTimes: PrayerTimes
  transparencyPeriods: Period[]; campaigns: Campaign[]; announcements: Announcement[]; committee: Member[]; donationEngineOn: boolean
}) {
  const { masjid, prayerTimes, transparencyPeriods, campaigns, announcements, committee, donationEngineOn } = props
  const place = [masjid.city, masjid.state].filter(Boolean).join(', ')
  const canDonate = donationEngineOn && masjid.verified === true && !!(masjid.upiId && masjid.upiId.trim())
  const latest = transparencyPeriods[0] || null
  const deficit = !!latest && latest.balance < 0
  const expenseCats = latest ? latest.categoryTotals.filter((c) => c.type === 'expense' && c.total > 0).sort((a, b) => b.total - a.total) : []
  const campaign = campaigns[0] || null
  const camp = campaign ? {
    ...campaign,
    pct: campaign.goal > 0 ? Math.min(100, Math.round((campaign.raised / campaign.goal) * 100)) : 0,
    days: campaign.endsAt ? Math.max(0, Math.ceil((new Date(campaign.endsAt).getTime() - Date.now()) / 86400000)) : null,
  } : null

  // Live next-prayer + countdown (client-only) ------------------------------------------------
  const rows = useMemo(() => ([
    { name: 'Fajr', t: prayerTimes?.Fajr }, { name: 'Dhuhr', t: prayerTimes?.Dhuhr }, { name: 'Asr', t: prayerTimes?.Asr },
    { name: 'Maghrib', t: prayerTimes?.Maghrib }, { name: 'Isha', t: prayerTimes?.Isha },
  ]), [prayerTimes])
  const [countdown, setCountdown] = useState('')
  const [nextName, setNextName] = useState<string | null>(null)
  const [nextTime, setNextTime] = useState<string | undefined>(undefined)
  useEffect(() => {
    const tick = () => {
      const now = new Date()
      let picked: { name: string; t: string } | null = null
      for (const r of rows) {
        if (!r.t || !/^\d{1,2}:\d{2}/.test(r.t)) continue
        const [h, m] = r.t.split(':').map(Number)
        const d = new Date(); d.setHours(h, m, 0, 0)
        if (d > now) { picked = { name: r.name, t: r.t }; break }
      }
      if (!picked) { setNextName(null); setCountdown(''); setNextTime(undefined); return }
      const [h, m] = picked.t.split(':').map(Number)
      const d = new Date(); d.setHours(h, m, 0, 0)
      const diff = d.getTime() - now.getTime()
      const hh = Math.floor(diff / 3600000), mm = Math.floor((diff % 3600000) / 60000), ss = Math.floor((diff % 60000) / 1000)
      setNextName(picked.name); setNextTime(picked.t)
      setCountdown(`${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`)
    }
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id)
  }, [rows])
  const hasPrayer = rows.some((r) => r.t && /^\d{1,2}:\d{2}/.test(r.t))

  // Follow + Share --------------------------------------------------------------------------
  const [following, setFollowing] = useState(false)
  const [followBusy, setFollowBusy] = useState(false)
  const follow = async () => {
    if (followBusy || following) return
    setFollowBusy(true)
    try {
      const res = await fetch(`/api/masjids/${masjid.id}/follow`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      if (res.status === 401 || res.status === 403) { window.location.href = '/login'; return }
      if (res.ok) setFollowing(true)
    } catch { /* silent — no fake success */ } finally { setFollowBusy(false) }
  }
  const [shared, setShared] = useState(false)
  const share = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : ''
    try {
      if (typeof navigator !== 'undefined' && (navigator as any).share) await (navigator as any).share({ title: masjid.nameEnglish, url })
      else { await navigator.clipboard.writeText(url); setShared(true); setTimeout(() => setShared(false), 1600) }
    } catch { /* cancelled */ }
  }

  const facilities: string[] = Array.isArray(masjid.facilities) ? masjid.facilities.map(String) : []
  const mapsHref = masjid.latitude && masjid.longitude
    ? `https://www.google.com/maps/search/?api=1&query=${masjid.latitude},${masjid.longitude}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${masjid.nameEnglish} ${place}`)}`
  const photos: string[] = Array.isArray(masjid.photos) ? masjid.photos.map(String).filter((s) => s && s.trim()) : []
  const ytId = youtubeVideoId(masjid.youtubeUrl)
  const isDemo = !!masjid.isDemo
  const heroSrc = masjid.coverPhoto && String(masjid.coverPhoto).trim()
    ? String(masjid.coverPhoto)
    : `/api/masjids/${masjid.id}/photo?index=0&w=1600`

  return (
    <div className={`${styles.shell} ${styles.proPremium}`}>
      <main>
        {/* ── HERO (dark band — reuses the basic page's hero shell) ─────────────── */}
        <section className={styles.hero}>
          <div className={styles.heroMedia}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={heroSrc}
              alt={masjid.nameEnglish}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { (e.currentTarget as HTMLImageElement).src = masjidPlaceholder(masjid.slug) }}
            />
            <div className={styles.heroOverlay} />
            <div className={styles.heroArt} aria-hidden />
            <div className={styles.heroDots} aria-hidden />
          </div>

          <div className={styles.heroContent}>
            <div className={styles.heroCopy}>
              <div className={styles.kicker}>
                <StatusBadge tone="gold"><Award size={12} /> Masjid OS Pro</StatusBadge>
                {masjid.verified ? <StatusBadge tone="emerald"><ShieldCheck size={12} /> Committee Verified</StatusBadge> : null}
                {latest ? <StatusBadge tone="gold"><BadgeCheck size={12} /> Transparency Active</StatusBadge> : null}
              </div>
              <h1>{masjid.nameEnglish}</h1>
              {place ? <p className={styles.location}><MapPin size={16} /> {place}</p> : null}
              <p className={styles.summary}>{masjid.description || 'A living civic institution for prayer, learning, service, and transparent community support.'}</p>
              <div className={styles.heroActions}>
                <Button onClick={follow} disabled={followBusy || following} leftIcon={<Star size={16} />}>{following ? 'Following' : 'Follow masjid'}</Button>
                <Button variant="ghost" onClick={share} leftIcon={<Share2 size={16} />}>{shared ? 'Link copied' : 'Share'}</Button>
                <ButtonLink href={mapsHref} target="_blank" variant="ghost" leftIcon={<Navigation size={16} />}>Directions</ButtonLink>
              </div>
            </div>

            {/* Right hero panel — next prayer (dark), like the basic page's live panel */}
            <PremiumCard surface="dark" className={styles.livePanel}>
              <div className={styles.panelTopline}>
                <span><Clock size={15} /> Prayer status</span>
                <StatusBadge tone="emerald">Calculated</StatusBadge>
              </div>
              <div className={styles.nextPrayer}>
                <span>Next prayer</span>
                <strong>{hasPrayer ? (nextName ?? '—') : '—'}</strong>
                <p style={figure}>{hasPrayer && nextTime ? `${fmt12(nextTime)}${countdown ? ` · in ${countdown}` : ''} — ${masjid.city || 'Hyderabad'}` : 'Loading calculated times…'}</p>
              </div>
              {hasPrayer ? (
                <div className={styles.heroStats}>
                  {rows.map((r) => <MetricPill key={r.name} label={r.name} value={fmt12(r.t)} tone={r.name === nextName ? 'gold' : 'emerald'} />)}
                </div>
              ) : null}
            </PremiumCard>
          </div>
        </section>

        {/* ── BODY (light) — two columns: main + sidebar ───────────────────────── */}
        <div className={styles.content}>
          <div className={styles.proBody}>
            <div className={styles.proMain}>

              {/* TRANSPARENCY (+ BILL ALERT) */}
              <div className={deficit ? styles.proTp : undefined}>
                <DashboardSection title="Transparency" eyebrow={latest ? `Committee-verified · ${monthLabel(latest.period)}` : 'Committee-verified reporting'}>
                  {latest ? (
                    <>
                      <div style={{ display: 'flex', gap: 30, alignItems: 'center', flexWrap: 'wrap', padding: '4px 0 6px' }}>
                        <div style={{ minWidth: 150 }}>
                          <div style={{ fontSize: 12, color: SEC }}>Total Received</div>
                          <div style={{ ...figure, fontSize: 26, fontWeight: 700, color: GREEN, margin: '2px 0 10px' }}>{inrSpaced(latest.income)}</div>
                          <div style={{ fontSize: 11.5, color: MUT }}>Updated {dateLabel(latest.approvedAt) || monthLabel(latest.period)}</div>
                          <div style={{ marginTop: 12, fontSize: 12, color: SEC }}>Balance</div>
                          <div style={{ ...figure, fontSize: 18, fontWeight: 700, color: deficit ? DANGER : GOLD }}>{inrSpaced(latest.balance)}</div>
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          <Donut segments={expenseCats.map((c, i) => ({ value: c.total, color: segColors[i % segColors.length] }))} />
                        </div>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          {expenseCats.map((c, i) => (
                            <div key={c.category} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, padding: '5px 0' }}>
                              <span style={{ width: 9, height: 9, borderRadius: 3, background: segColors[i % segColors.length], flexShrink: 0 }} />
                              <span style={{ flex: 1, color: NAVY }}>{c.category}</span>
                              <span style={{ ...figure, color: NAVY, fontWeight: 700 }}>{inr(c.total)}</span>
                              <span style={{ ...figure, color: MUT, width: 46, textAlign: 'right' }}>{Math.round((c.total / (latest.expense || 1)) * 100)}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 13, borderTop: `1px solid ${LINE}` }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: GREEN, fontWeight: 600 }}><ShieldCheck size={14} strokeWidth={2} />100% to Masjid UPI Only</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: SEC }}>Verified by Masjid Committee<Award size={19} strokeWidth={1.5} style={{ color: GOLD_BRIGHT }} /></span>
                      </div>
                    </>
                  ) : <EmptyFrame icon={Target} title="Transparency Reporting Coming Soon" sub="This masjid hasn't published a monthly transparency report yet." />}
                </DashboardSection>

                {deficit && latest ? (
                  <DashboardSection title="Bill Alert" eyebrow="Approved monthly shortfall">
                    <p style={{ margin: '0 0 4px', fontSize: 12.5, color: SEC }}>We&apos;re short this month</p>
                    <p style={{ ...figure, margin: '0 0 6px', fontSize: 30, fontWeight: 700, color: DANGER }}>{inrSpaced(Math.abs(latest.balance))}</p>
                    <p style={{ margin: '0 0 14px', fontSize: 12.5, color: SEC, lineHeight: 1.5 }}>Help us clear the shortfall and keep the masjid running.</p>
                    {canDonate ? <DonateFlow masjidId={masjid.id} masjidName={masjid.nameEnglish} /> : null}
                    <p style={{ margin: '12px 0 0', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: GREEN, fontWeight: 600 }}><ShieldCheck size={13} strokeWidth={2} />100% to Masjid UPI Only</p>
                  </DashboardSection>
                ) : null}
              </div>

              {/* ACTIVE CAMPAIGN + LIVE ACTIVITY */}
              <div className={styles.proCa}>
                <DashboardSection title="Active Campaign" eyebrow="Live fundraising">
                  {camp ? (
                    <>
                      <p style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 700, color: NAVY }}>{camp.title}</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13, marginBottom: 6 }}>
                        <span><span style={{ ...figure, fontWeight: 700, color: GREEN }}>{inrSpaced(camp.raised)}</span><span style={{ color: SEC }}> of {inrSpaced(camp.goal)}</span></span>
                        <span style={{ ...figure, fontWeight: 700, color: NAVY }}>{camp.pct}%</span>
                      </div>
                      <div style={{ height: 8, borderRadius: 999, background: 'rgba(11,19,43,.06)', overflow: 'hidden', marginBottom: 12 }}>
                        <span style={{ display: 'block', height: '100%', width: `${camp.pct}%`, background: 'linear-gradient(90deg, #0E7C61, #14B8A6)', borderRadius: 999 }} />
                      </div>
                      {camp.days != null ? <p style={{ margin: '0 0 14px', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: SEC }}><Clock size={14} strokeWidth={1.75} /><span style={figure}>{camp.days}</span> days left</p> : null}
                      <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
                        {canDonate ? <div style={{ flex: '1 1 auto', minWidth: 0 }}><DonateFlow masjidId={masjid.id} masjidName={masjid.nameEnglish} /></div> : null}
                        <Button variant="ghost" onClick={share} leftIcon={<Share2 size={15} />}>Share</Button>
                      </div>
                    </>
                  ) : <EmptyFrame icon={Target} title="No Active Campaigns" sub="This masjid currently has no active fundraising campaigns." />}
                </DashboardSection>

                <DashboardSection title="Live Activity" eyebrow="Updates from this masjid">
                  {announcements.length ? (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {announcements.map((a, i) => {
                        const meta = ACT[a.category] || ACT.general
                        return (
                          <div key={a.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderTop: i ? `1px solid ${LINE}` : 'none' }}>
                            <span style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, display: 'grid', placeItems: 'center', background: `${meta.color}1a`, border: `1px solid ${meta.color}40` }}>
                              <Zap size={16} strokeWidth={1.75} style={{ color: meta.color }} />
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ margin: 0, fontSize: 13.5, color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</p>
                              <p style={{ margin: '2px 0 0', fontSize: 11.5, color: SEC }}>{meta.label}</p>
                            </div>
                            {a.createdAt ? <span style={{ ...figure, fontSize: 11.5, color: MUT, flexShrink: 0 }}>{relTime(a.createdAt)}</span> : null}
                          </div>
                        )
                      })}
                    </div>
                  ) : <EmptyFrame icon={Zap} title="No Recent Activity" sub="Updates from this masjid will appear here." />}
                </DashboardSection>
              </div>

              {/* WATCH — real youtube-nocookie embed; honest-empty (card hidden) when no video URL */}
              {ytId ? (
                <DashboardSection title="Watch" eyebrow="Video from this masjid">
                  <div className={styles.proVideoFrame}>
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${ytId}`}
                      title={`${masjid.nameEnglish} video`}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                </DashboardSection>
              ) : null}

              {/* GET INVOLVED (PEOPLE OS) */}
              <DashboardSection title="Get Involved" eyebrow="People OS · serve & support">
                <div className={styles.proTiles}>
                  {[
                    { t: 'Offer Khidmah', d: 'Use your skills to serve the community.', c: 'I Can Help', href: '/connect', icon: HeartHandshake, gold: false },
                    { t: 'Request Help', d: 'Need help in any area? We\'re here for you.', c: 'Request Help', href: '/connect/discover', icon: HandHelping, gold: true },
                    { t: 'Volunteer', d: 'Join volunteers and make a difference.', c: 'Volunteer', href: `/masjids/${masjid.slug}/volunteer`, icon: Users, gold: false },
                  ].map((x) => (
                    <PremiumCard key={x.t} className="premium-card-light" surface="light">
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <span style={{ width: 44, height: 44, borderRadius: 12, display: 'grid', placeItems: 'center', background: x.gold ? 'rgba(212,175,55,.12)' : 'rgba(14,124,97,.10)', border: `1px solid ${x.gold ? 'rgba(212,175,55,.3)' : 'rgba(14,124,97,.22)'}` }}>
                          <x.icon size={20} strokeWidth={1.5} style={{ color: x.gold ? GOLD : GREEN }} />
                        </span>
                        <h3 style={{ margin: '11px 0 4px' }}>{x.t}</h3>
                        <p style={{ margin: '0 0 13px', minHeight: 34 }}>{x.d}</p>
                        <ButtonLink href={x.href} variant={x.gold ? 'gold' : 'primary'} size="sm" className={styles.fullBtn}>{x.c}</ButtonLink>
                      </div>
                    </PremiumCard>
                  ))}
                  <PremiumCard className="premium-card-light" surface="light">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <span style={{ width: 44, height: 44, borderRadius: 12, display: 'grid', placeItems: 'center', background: 'rgba(212,175,55,.12)', border: '1px solid rgba(212,175,55,.3)' }}>
                        <UserPlus size={20} strokeWidth={1.5} style={{ color: GOLD }} />
                      </span>
                      <h3 style={{ margin: '11px 0 4px' }}>Become a Member</h3>
                      <p style={{ margin: '0 0 13px', minHeight: 34 }}>Become a member of this jama&apos;ah.</p>
                      <Button variant="gold" size="sm" onClick={follow} disabled={followBusy || following} className={styles.fullBtn}>{following ? 'Member' : followBusy ? 'Joining…' : "Join Jama'ah"}</Button>
                    </div>
                  </PremiumCard>
                </div>
              </DashboardSection>

              {/* ABOUT + LOCATION + COMMITTEE */}
              <div className={styles.proTriple}>
                <DashboardSection title="About" eyebrow="Masjid profile">
                  <p style={{ margin: '0 0 13px', fontSize: 13, color: SEC, lineHeight: 1.65 }}>{masjid.description || 'This masjid has not added an about description yet.'}</p>
                  {facilities.length ? (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {facilities.slice(0, 6).map((f) => {
                        const Icon = FACILITY_ICON[f.toLowerCase()] || Star
                        return (
                          <span key={f} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: NAVY, background: 'var(--color-ivory-200, #F0EDE4)', border: `1px solid ${LINE}`, padding: '5px 10px', borderRadius: 8 }}>
                            <Icon size={13} strokeWidth={1.5} style={{ color: GREEN }} />{f}
                          </span>
                        )
                      })}
                    </div>
                  ) : null}
                </DashboardSection>

                <DashboardSection title="Location" eyebrow="Find & visit">
                  <p style={{ margin: '0 0 12px', fontSize: 13, color: SEC, lineHeight: 1.6 }}>{[masjid.addressLine1, place].filter(Boolean).join(', ') || 'Location not set.'}</p>
                  <div style={{ height: 92, borderRadius: 10, background: 'radial-gradient(120px 70px at 50% 40%, rgba(14,124,97,.10), transparent 70%), var(--color-ivory-200, #F0EDE4)', border: `1px solid ${LINE}`, display: 'grid', placeItems: 'center', marginBottom: 12 }}>
                    <MapPin size={26} strokeWidth={1.5} style={{ color: GREEN }} />
                  </div>
                  <ButtonLink href={mapsHref} target="_blank" variant="gold" size="sm" className={styles.fullBtn} leftIcon={<Navigation size={14} />}>Directions</ButtonLink>
                </DashboardSection>

                <DashboardSection title="Committee" eyebrow="Institutional trust">
                  {committee.length ? (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 4 }}>
                        {committee.slice(0, 8).map((m, i) => (
                          <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'center', minWidth: 0 }}>
                            {/* Pro/waqf: real photo when the committee has uploaded one; else the
                                honest-empty initials monogram (never a broken image / stock face). */}
                            {m.photoUrl ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img src={m.photoUrl} alt="" width={34} height={34} style={{ width: 34, height: 34, borderRadius: 999, flexShrink: 0, objectFit: 'cover', border: '1px solid rgba(212,175,55,.3)' }} />
                            ) : (
                              <span style={{ width: 34, height: 34, borderRadius: 999, flexShrink: 0, display: 'grid', placeItems: 'center', background: 'rgba(212,175,55,.12)', border: '1px solid rgba(212,175,55,.3)', fontSize: 12, fontWeight: 700, color: GOLD }}>{initials(m.name)}</span>
                            )}
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                              <div style={{ fontSize: 11, color: SEC }}>{m.label}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <ButtonLink href="/connect" variant="outline" size="sm" className={styles.fullBtn}>Contact Committee</ButtonLink>
                      <p style={{ margin: '9px 0 0', fontSize: 11, color: MUT, textAlign: 'center' }}>Your request will be reviewed by the committee.</p>
                    </>
                  ) : <EmptyFrame icon={Users} title="Committee Not Added" sub="Committee information will appear once added by the masjid." />}
                </DashboardSection>
              </div>
            </div>

            {/* ── RIGHT SIDEBAR (web) ─────────────────────────────────────────── */}
            <aside className={styles.proSide}>
              <DashboardSection title="Quick Actions" eyebrow="Support this masjid">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {canDonate ? <DonateFlow masjidId={masjid.id} masjidName={masjid.nameEnglish} /> : null}
                  <Button onClick={follow} disabled={followBusy || following} leftIcon={<Heart size={15} />} className={styles.fullBtn}>{following ? 'Following' : 'Follow Masjid'}</Button>
                  <Button variant="ghost" onClick={share} leftIcon={<Share2 size={15} />} className={styles.fullBtn}>{shared ? 'Link copied' : 'Share'}</Button>
                  <ButtonLink href="/connect" variant="ghost" size="md" className={styles.fullBtn} leftIcon={<Users size={15} />}>Contact Committee</ButtonLink>
                </div>
                {canDonate ? <p style={{ margin: '11px 0 0', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: GREEN, fontWeight: 600 }}><ShieldCheck size={13} strokeWidth={2} />100% to Masjid UPI Only</p> : null}
              </DashboardSection>

              <DashboardSection title="Snapshot" eyebrow="Live figures">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
                  {hasPrayer ? (
                    <div>
                      <div style={{ fontSize: 11, color: MUT, marginBottom: 5 }}>Next prayer</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>{nextName ?? '—'}</span>
                        <span style={{ ...figure, fontSize: 15, fontWeight: 700, color: GOLD }}>{fmt12(nextTime)}</span>
                      </div>
                      <div style={{ ...figure, fontSize: 11.5, color: MUT }}>{countdown ? `in ${countdown}` : ''}</div>
                    </div>
                  ) : null}
                  {latest ? (
                    <div style={{ paddingTop: hasPrayer ? 13 : 0, borderTop: hasPrayer ? `1px solid ${LINE}` : 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ fontSize: 11, color: MUT, marginBottom: 2 }}>Transparency · {monthLabel(latest.period).replace(/ \d{4}$/, '')}</div>
                      <StatRow label="Received" value={inrSpaced(latest.income)} color={GREEN} />
                      <StatRow label="Spent" value={inrSpaced(latest.expense)} />
                      <StatRow label="Balance" value={inrSpaced(latest.balance)} color={deficit ? DANGER : GOLD} />
                    </div>
                  ) : null}
                  {camp ? (
                    <div style={{ paddingTop: (hasPrayer || latest) ? 13 : 0, borderTop: (hasPrayer || latest) ? `1px solid ${LINE}` : 'none' }}>
                      <div style={{ fontSize: 11, color: MUT, marginBottom: 6 }}>Active campaign</div>
                      <div style={{ fontSize: 12.5, color: NAVY, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{camp.title}</div>
                      <div style={{ height: 7, borderRadius: 999, background: 'rgba(11,19,43,.06)', overflow: 'hidden', marginBottom: 5 }}>
                        <span style={{ display: 'block', height: '100%', width: `${camp.pct}%`, background: 'linear-gradient(90deg, #0E7C61, #14B8A6)', borderRadius: 999 }} />
                      </div>
                      <div style={{ ...figure, display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: MUT }}>
                        <span>{inr(camp.raised)} of {inr(camp.goal)}</span><span>{camp.pct}%</span>
                      </div>
                    </div>
                  ) : null}
                  {!hasPrayer && !latest && !camp ? <p style={{ margin: 0, fontSize: 12.5, color: SEC }}>Live stats will appear here as this masjid publishes them.</p> : null}
                </div>
              </DashboardSection>

              {/* Instagram + Facebook feeds — honest real-or-empty (see SocialFeed) */}
              <SocialFeed platform="Instagram" url={masjid.instagramUrl} icon={Instagram} tint="#C13584" isDemo={isDemo} samples={photos} />
              <SocialFeed platform="Facebook" url={masjid.facebookUrl} icon={Facebook} tint="#1877F2" isDemo={isDemo} samples={photos.slice().reverse()} />
            </aside>
          </div>
        </div>
      </main>
    </div>
  )
}
